import React, { useState, useEffect } from 'react';
import {
  Row,
  Form,
  Divider,
  Button,
  Drawer,
  Input,
  Switch,
  Select,
  Alert
} from 'antd';
import {
  api,
  FormItemWrap,
  configManager,
  useCrudEditContext,
  generateUUID,
  useAuthContext
} from 'react-structure-admin';
import {
  InputNumeric,
  InputPercent,
  ResCol,
  SearchInput,
  TaxCodeSelect
} from 'components';
import SelectTaxationType from 'components/SelectTaxationType';
import SelectProvisionMode from 'components/SelectProvisionMode';
import SelectLinkBetweenPart from 'components/SelectLinkBetweenPart';
import SelectLinkOperation from 'components/SelectLinkOperation';
import SelectProvisionSupportMechanism from 'components/SelectProvisionSupportMechanism';
import SelectBorrowerSupportMechanism from 'components/SelectBorrowerSupportMechanism';
import SelectServiceInvoiceLocation from 'components/SelectServiceInvoiceLocation';
import SelectServiceInvoiceTaxLocation from 'components/SelectServiceInvoiceTaxLocation';
import SelectCstPisCofins from 'components/SelectCstPisCofins';
import TaxationGroupServiceInvoiceConditionList from './TaxationGroupServiceInvoiceConditionList';
import SelectImmunityType from 'components/SelectImmunityType';
import SelectServiceInvoiceMunicipalBenefitType from 'components/SelectServiceInvoiceMunicipalBenefitType';
import SelectServiceInvoiceIbsCbsOperationType from 'components/SelectServiceInvoiceIbsCbsOperationType';

const supportedLocationTypes = [
  'companyMunicipality',
  'outsideCompanyMunicipality',
  'foreign'
];

const TaxationGroupServiceInvoiceRuleEdit = ({ editingRule, onClose }) => {
  const [form] = Form.useForm();
  const { company } = useAuthContext();
  const { form: mainForm } = useCrudEditContext();

  const [issueType, setIssueType] = useState(editingRule.issueType);
  const [providerSettings, setProviderSettings] = useState({});
  const [locationType, setLocationType] = useState(
    editingRule.conditions?.locationTypes ?? supportedLocationTypes
  );

  const [showImmunityType, setShowImmunityType] = useState(false);

  let {
    numberDecimalPlaces,
    supportsTaxReform,
    useNationalLayout
  } = providerSettings;

  useEffect(() => {
    setShowImmunityType(
      editingRule.taxationType === 'immune' && useNationalLayout
    );
  }, [issueType]);

  const isEditing =
    editingRule?.id !== undefined || editingRule?.uuid !== undefined;

  const handleInsertOrUpdate = (values) => {
    if (!isEditing) {
      values.uuid = generateUUID();
    }

    const { rules = [] } = mainForm.getFieldsValue();
    const key = editingRule?.id ?? editingRule?.uuid;

    const index = rules.findIndex(
      (c) =>
        (c?.id !== undefined && c.id === key) ||
        (c?.uuid !== undefined && c.uuid) === key
    );

    if (index > -1) {
      rules[index] = values;
    } else {
      rules.push(values);
    }

    mainForm.setFieldsValue({ rules: [...rules] });
    form.resetFields();
    onClose();
  };

  const handleBeforeSave = (rule) => {
    const { conditions = {}, municipalBenefit = {} } = rule;
    const hasOperator =
      conditions?.operator !== undefined && conditions?.operator !== null;

    return {
      ...rule,
      municipalBenefit: {
        ...municipalBenefit,
        identification: municipalBenefit?.identification?.trim()
          ? municipalBenefit.identification
          : null
      },
      conditions: {
        ...conditions,
        operator: hasOperator ? conditions?.operator : null,
        amount: hasOperator ? conditions?.amount : null
      }
    };
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (!values.conditions || Object.keys(values.conditions).length === 0) {
        values.conditions = null;
      }

      values = handleBeforeSave({ ...editingRule, ...values });
      handleInsertOrUpdate(values);
    });
  };

  const handleBeforeBinding = (rule) => {
    const { conditions = {} } = rule;

    return {
      ...rule,
      conditions: {
        ...conditions,
        amount: conditions?.operator ? conditions?.amount : null
      }
    };
  };

  useEffect(() => {
    api.fetch(`companies/${company.id}/settings`).then(({ data }) => {
      const { settings, providerSettings } = data.result;

      setProviderSettings(providerSettings);
      setIssueType(settings.serviceInvoice.issueType);
    });
  }, []);

  const initialValues = handleBeforeBinding(editingRule);

  const handleChangedValues = (changedValues, _) => {
    if (changedValues.taxationType) {
      setShowImmunityType(
        changedValues.taxationType == 'immune' && useNationalLayout
      );
    }
  };

  return (
    <Drawer
      visible
      width={numberDecimalPlaces > 2 ? 750 : 680}
      title="Regra NFS-e"
      bodyStyle={{ paddingBottom: 80 }}
      onClose={onClose}
      footer={
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: '100%',
            borderTop: '1px solid #e9e9e9',
            padding: '10px 16px',
            background: '#fff',
            textAlign: 'right'
          }}
        >
          <Button onClick={onClose}>Voltar</Button>
          <Button type="primary" onClick={handleSubmit}>
            Salvar
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        {...configManager.getConfig().layout.form}
        initialValues={initialValues}
        onValuesChange={handleChangedValues}
      >
        <Divider orientation="left">Condições</Divider>
        <TaxationGroupServiceInvoiceConditionList
          company={company}
          form={form}
          onLocationTypeChange={(value) => setLocationType(value)}
        />

        <Divider orientation="left">Tributação</Divider>
        <Row>
          <FormItemWrap name={['producer', 'id']} hidden>
            <Input />
          </FormItemWrap>
          <ResCol span={showImmunityType ? 12 : 24}>
            <FormItemWrap label="Natureza da Operação" name={['taxationType']}>
              <SelectTaxationType />
            </FormItemWrap>
          </ResCol>
          {showImmunityType && (
            <ResCol span={12}>
              <FormItemWrap label="Tipo da imunidade" name={['immunityType']}>
                <SelectImmunityType />
              </FormItemWrap>
            </ResCol>
          )}
        </Row>
        <Row>
          <ResCol span={6}>
            <FormItemWrap label="ISS" name={['issRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="PIS" name={['pisRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="COFINS" name={['cofinsRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="IR" name={['irRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={6}>
            <FormItemWrap
              name={['issWithheld']}
              label="Reter ISS?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap
              name={['pisWithheld']}
              label="Reter PIS?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap
              name={['cofinsWithheld']}
              label="Reter COFINS?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap
              name={['irWithheld']}
              label="Reter IR?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={6}>
            <FormItemWrap label="INSS" name={['inssRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="CSLL" name={['csllRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="Deduções" name={['deductionsRate']}>
              <InputPercent decimalPlaces={numberDecimalPlaces} />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={6}>
            <FormItemWrap
              name={['inssWithheld']}
              labelAlign="center"
              label="Reter INSS?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap
              name={['csllWithheld']}
              label="Reter CSLL?"
              valuePropName="checked"
            >
              <Switch />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={24}>
            <FormItemWrap
              label="Informações adicionais"
              name={['additionalInformation']}
              whitespace
            >
              <Input.TextArea maxLength={2000} rows={3} />
            </FormItemWrap>
          </ResCol>
        </Row>
        <Row>
          <ResCol span={12}>
            <FormItemWrap
              label="Local de prestação do serviço"
              name={['locationServiceProvision']}
              initialValue="companyMunicipality"
            >
              <SelectServiceInvoiceLocation />
            </FormItemWrap>
          </ResCol>

          <ResCol span={12}>
            <FormItemWrap
              label="Município de incidência"
              name={['taxLocation']}
              initialValue="companyMunicipality"
            >
              <SelectServiceInvoiceTaxLocation />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Divider orientation="left">PIS/COFINS</Divider>
        <Row>
          <ResCol span={12}>
            <FormItemWrap label="CST" name={['cstPisCofins']}>
              <SelectCstPisCofins
                valueType="string"
                showNoneOption
                allowClear
              />
            </FormItemWrap>
          </ResCol>
        </Row>

        {issueType === 'annfs' && (
          <>
            <Divider orientation="left">Benefício Municipal</Divider>
            <Row>
              <ResCol span={12}>
                <FormItemWrap label="Tipo" name={['municipalBenefit', 'type']}>
                  <SelectServiceInvoiceMunicipalBenefitType />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  name={['municipalBenefit', 'identification']}
                  label="Identificação"
                  dependencies={[['municipalBenefit', 'type']]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          value?.trim() ||
                          !getFieldValue(['municipalBenefit', 'type'])
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error('Informe a identificação do benefício')
                        );
                      }
                    })
                  ]}
                >
                  <InputNumeric maxLength={14} />
                </FormItemWrap>
              </ResCol>
            </Row>
          </>
        )}

        {company.taxReformFieldsEnabled && (
          <>
            <Divider orientation="left">IBS/CBS</Divider>
            <Row>
              <ResCol span={12}>
                <FormItemWrap name={['ibsCbs', 'cst']} label="CST" required>
                  <TaxCodeSelect type="cstIbsCbs" />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <SearchInput
                  name={['ibsCbs', 'classification']}
                  label="Cód. Classificação Tributária"
                  title="Cód. Classificação Tributária"
                  resource="tax-class-codes"
                  span={24}
                  drawerSize="30%"
                  rules={[{ required: true, message: 'Selecione um código' }]}
                />
              </ResCol>
            </Row>
            <Row>
              <ResCol span={12}>
                <FormItemWrap
                  name={['ibsCbs', 'operationIndicatorCode']}
                  label="Cód. Indicador Operação"
                  required
                >
                  <Input maxLength={6} />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  name={['ibsCbs', 'isPersonalUse']}
                  label="Uso ou consumo pessoal"
                >
                  <Select defaultValue={true}>
                    <Select.Option value={true}>Sim</Select.Option>
                    <Select.Option value={false}>Não</Select.Option>
                  </Select>
                </FormItemWrap>
              </ResCol>
            </Row>
            <Row>
              <ResCol span={24}>
                <FormItemWrap
                  name={['ibsCbs', 'operationType']}
                  label="Tipo de Operação com Entes Governamentais"
                  tooltip={{
                    title: (
                      <div>
                        Campo obrigatório apenas quando o Item da Lista de
                        Serviço ou o Código de Tributação Nacional começar
                        com 10.05, 17.12, 15.09 ou 25.05.
                      </div>
                    ),
                    placement: 'right'
                  }}
                >
                  <SelectServiceInvoiceIbsCbsOperationType placement="topLeft" />
                </FormItemWrap>
              </ResCol>
            </Row>

            {!supportsTaxReform && (
              <Alert
                style={{ marginTop: '10px' }}
                type="info"
                message={
                  <span>
                    Estamos trabalhando na adaptação da integração com o seu
                    município à <strong>Reforma Tributária</strong>. <br />
                    <a href={''} target="_blank">
                      Clique aqui
                    </a>{' '}
                    para saber como estamos conduzindo esse processo.
                  </span>
                }
                closable={false}
              />
            )}
          </>
        )}

        {locationType?.includes('foreign') ? (
          <>
            <Divider orientation="left">
              Informações de Comércio Exterior
            </Divider>
            <Row>
              <ResCol span={12}>
                <FormItemWrap
                  label="Modo de prestação"
                  name={['serviceProvisionMode']}
                >
                  <SelectProvisionMode />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  label="Vínculo entre as partes"
                  name={['relationshipBetweenParties']}
                >
                  <SelectLinkBetweenPart />
                </FormItemWrap>
              </ResCol>
            </Row>
            <Row>
              <ResCol span={12}>
                <FormItemWrap
                  label="Fomento utilizado pelo prestador do serviço"
                  name={['supportMechanismForServiceProvider']}
                >
                  <SelectProvisionSupportMechanism />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  label="Fomento utilizado pelo tomador do serviço"
                  name={['supportMechanismForBorrower']}
                >
                  <SelectBorrowerSupportMechanism />
                </FormItemWrap>
              </ResCol>
            </Row>
            <Row>
              <ResCol span={16}>
                <FormItemWrap
                  label="Vínculo da Operação à Movimentação Temporária de Bens"
                  name={['relationshipBetweenOperationAndTemporaryMovement']}
                >
                  <SelectLinkOperation />
                </FormItemWrap>
              </ResCol>
              <ResCol span={8}>
                <FormItemWrap
                  name={['sendInvoiceToMdic']}
                  label="Enviar NFS-e ao MDIC?"
                  valuePropName="checked"
                >
                  <Switch />
                </FormItemWrap>
              </ResCol>
            </Row>
          </>
        ) : null}
      </Form>
    </Drawer>
  );
};

export default TaxationGroupServiceInvoiceRuleEdit;
